import React, { useEffect, useState } from "react";
import { Input, Modal } from "antd";
import { Eye, CheckCircle, XCircle } from "lucide-react";
import { useAllOrders } from "../../../../../../api/orders/orders";
import {
  useVerifyOrder,
  useDeclineOrder,
} from "../../../../../../api/orders/orders";
import { jwtDecode } from "jwt-decode";

const ORDERS_PER_PAGE = 20;

const PendingOrder = () => {
  const [searchText, setSearchText] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false);
  const [declineRemarks, setDeclineRemarks] = useState("");
  const [orderToDecline, setOrderToDecline] = useState(null);
  const [userType, setUserType] = useState("user");
  const [userId, setUserId] = useState(null);

  const BACKEND_URL = "https://top.teamexbiz.in/api";

  // Mutations
  const { mutate: verifyOrder, isLoading: isVerifying } = useVerifyOrder();
  const { mutate: declineOrder, isLoading: isDeclining } = useDeclineOrder();

  const {
    data: allOrdersData,
    isLoading: isAllLoading,
    isError,
    isFetching,
  } = useAllOrders({
    currentPage,
    ORDERS_PER_PAGE,
    verify: false,
    decline: false,
    search: searchQuery,
  });

  // Debounce search
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearchQuery(searchText.trim());
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [searchText]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        const role = decoded.role
          ? decoded.role.toString().trim().toLowerCase()
          : "user";
        setUserType(role);
        setUserId(decoded.id || null);
      } catch (error) {
        console.error("Failed to decode token:", error);
        setUserType("user");
        setUserId(null);
      }
    } else {
      setUserType("user");
      setUserId(null);
    }
  }, []);

  const orders = allOrdersData?.orders || [];
  const totalPages = allOrdersData?.totalPages || 1;
  const isLoading = isAllLoading;

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setIsModalOpen(true);
  };

  const handleVerify = (orderId) => {
    verifyOrder(orderId);
  };
  const getPaymentByOption = (order, optionName) => {
    if (!order.paymentMethods || order.paymentMethods.length === 0) return 0;
    const method = order.paymentMethods.find(
      (m) => m.paymentOption?.toLowerCase() === optionName.toLowerCase()
    );
    return method ? method.paymentAmount || 0 : 0;
  };
  const handleDeclineClick = (orderId) => {
    setOrderToDecline(orderId);
    setDeclineRemarks("");
    setIsDeclineModalOpen(true);
  };

  const handleDeclineSubmit = () => {
    if (!declineRemarks.trim()) {
      return;
    }

    declineOrder(
      { orderId: orderToDecline, remarks: declineRemarks },
      {
        onSuccess: () => {
          setIsDeclineModalOpen(false);
          setDeclineRemarks("");
          setOrderToDecline(null);
        },
      }
    );
  };

  // Helper function to get payment info
  const getPaymentInfo = (order) => {
    if (order.paymentMethods && order.paymentMethods.length > 0) {
      return {
        option: order.paymentMethods.map((m) => m.paymentOption).join(", "),
        amount: order.paymentTotalAmount || 0,
      };
    }
    return {
      option: order.paymentOption || "N/A",
      amount: order.paymentAmount || 0,
    };
  };

  // Helper function to get all payment proof images
  const getAllPaymentImages = (order) => {
    if (order.paymentMethods && order.paymentMethods.length > 0) {
      const allImages = [];
      order.paymentMethods.forEach((method) => {
        if (method.paymentProofImages && method.paymentProofImages.length > 0) {
          allImages.push(...method.paymentProofImages);
        }
      });
      return allImages;
    }
    return order.paymentProofImages || [];
  };

  if (isError)
    return <p className="text-red-600 mt-4">Error fetching pending orders</p>;

  return (
    <div className="w-full mt-6">
      {/* Search */}
      <div className="mb-4 flex justify-between items-center">
        <Input
          className="w-80"
          placeholder="Search by Order Number or Customer Name"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
        />
        <div className="text-sm text-gray-600">
          Total Pending: <strong>{allOrdersData?.totalOrders || 0}</strong>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto hide-scrollbar">
        <table className="min-w-[1200px] w-full table-auto border border-gray-200">
          <thead className="bg-gray-100 text-left text-sm font-semibold text-gray-700">
            <tr>
              <th className="p-3">Order Date</th>
              <th className="p-3">Order Number</th>
              <th className="p-3">Customer</th>
              <th className="p-3">Swapnil Gautam</th>
              <th className="p-3">ClicktoCart</th>
              <th className="p-3">Invoice Amount</th>
              <th className="p-3">Status</th>
              <th className="p-3">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white text-sm">
            {isLoading ? (
              <tr>
                <td colSpan={8} className="text-center py-6 text-gray-500">
                  Loading pending orders...
                </td>
              </tr>
            ) : orders.length > 0 ? (
              orders.map((order, index) => {
                const swapnilAmount = getPaymentByOption(
                  order,
                  "Swapnil Gautam"
                );
                const clickToCartAmount = getPaymentByOption(
                  order,
                  "ClickToCart"
                );
                return (
                  <tr
                    key={order._id || index}
                    className="hover:bg-gray-50 border-b"
                  >
                    <td className="p-4">
                      {new Date(order.createdAt).toLocaleDateString()}
                    </td>
                    <td className="p-4">
                      <div className="font-medium text-blue-600">
                        {order.order_number}
                      </div>
                    </td>
                    <td className="p-4">
                      <p className="font-medium">{order.CustomerName}</p>
                      <p className="text-xs text-gray-500">
                        {order.ShippingAddress?.Contact ||
                          order.BillingAddress?.Contact}
                      </p>
                    </td>
                    <td className="p-4 text-green-600 font-semibold">
                      ₹{swapnilAmount.toFixed(2)}
                    </td>
                    <td className="p-4 text-blue-600 font-semibold">
                      ₹{clickToCartAmount.toFixed(2)}
                    </td>
                    <td className="p-4">
                      ₹{order.total_amount?.toFixed(2) || 0}
                    </td>
                    <td className="p-4">
                      <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs font-medium">
                        Pending Verification
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleViewDetails(order)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded disabled:opacity-50"
                          title="View Details"
                          disabled={isVerifying || isDeclining}
                        >
                          <Eye size={18} />
                        </button>
                        {userType === "admin" && (
                          <>
                            <button
                              onClick={() => handleVerify(order._id)}
                              className="p-2 text-green-600 hover:bg-green-50 rounded disabled:opacity-50"
                              title="Verify Payment"
                              disabled={isVerifying || isDeclining}
                            >
                              <CheckCircle size={18} />
                            </button>
                            <button
                              onClick={() => handleDeclineClick(order._id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                              title="Decline Payment"
                              disabled={isVerifying || isDeclining}
                            >
                              <XCircle size={18} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={8} className="text-center py-6 text-gray-400">
                  No pending orders found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center mt-6 text-sm text-gray-700">
          <p>
            Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
          </p>
          <div className="space-x-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1 || isFetching}
              className={`px-4 py-2 rounded border ${
                currentPage === 1 || isFetching
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white hover:bg-gray-50"
              }`}
            >
              Previous
            </button>
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages || isFetching}
              className={`px-4 py-2 rounded border ${
                currentPage === totalPages || isFetching
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white hover:bg-gray-50"
              }`}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Decline Remarks Modal */}
      <Modal
        title="Decline Payment - Provide Reason"
        open={isDeclineModalOpen}
        onCancel={() => {
          setIsDeclineModalOpen(false);
          setDeclineRemarks("");
          setOrderToDecline(null);
        }}
        footer={[
          <button
            key="cancel"
            onClick={() => {
              setIsDeclineModalOpen(false);
              setDeclineRemarks("");
              setOrderToDecline(null);
            }}
            className="px-4 py-2 border border-gray-300 rounded text-gray-600 hover:bg-gray-100 mr-2"
            disabled={isDeclining}
          >
            Cancel
          </button>,
          <button
            key="submit"
            onClick={handleDeclineSubmit}
            disabled={isDeclining || !declineRemarks.trim()}
            className="px-6 py-2 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isDeclining ? "Declining..." : "Decline Payment"}
          </button>,
        ]}
        width={500}
      >
        <div className="py-4">
          <label className="block mb-2 font-semibold text-gray-700">
            Reason for Declining Payment <span className="text-red-500">*</span>
          </label>
          <Input.TextArea
            value={declineRemarks}
            onChange={(e) => setDeclineRemarks(e.target.value)}
            placeholder="Enter the reason for declining this payment (e.g., Invalid payment proof, Incorrect amount, etc.)"
            rows={4}
            maxLength={500}
            showCount
            className="w-full"
          />
        </div>
      </Modal>

      {/* Details Modal */}
      <Modal
        title={`Order Details - ${selectedOrder?.order_number}`}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        width={800}
      >
        {selectedOrder && (
          <div className="space-y-4">
            {/* Customer Info */}
            <div className="border-b pb-4">
              <h3 className="font-semibold mb-2">Customer Information</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Name:</span>{" "}
                  <span className="font-medium">
                    {selectedOrder.CustomerName}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Contact:</span>{" "}
                  <span className="font-medium">
                    {selectedOrder.ShippingAddress?.phone ||
                      selectedOrder.BillingAddress?.phone}
                  </span>
                </div>
                <div className="col-span-2">
                  <span className="text-gray-600">Email:</span>{" "}
                  <span className="font-medium">
                    {selectedOrder.ShippingAddress?.email || "N/A"}
                  </span>
                </div>
              </div>
            </div>

            {/* Shipping & Courier Info */}
            <div className="border-b pb-4">
              <h3 className="font-semibold mb-2">
                Shipping & Courier Information
              </h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">AWB Number:</span>{" "}
                  <span className="font-medium text-blue-600">
                    {selectedOrder.awbNumber || "N/A"}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Courier:</span>{" "}
                  <span className="font-medium">
                    {selectedOrder.courier || "N/A"}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Order Status:</span>{" "}
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                    {selectedOrder.shippingStatus
                      ?.replace(/_/g, " ")
                      .toUpperCase() || "N/A"}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Manifest Status:</span>{" "}
                  <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
                    {selectedOrder.manifestStatus
                      ?.replace(/_/g, " ")
                      .toUpperCase() || "N/A"}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Shipping Charge:</span>{" "}
                  <span className="font-medium text-green-600">
                    ₹
                    {selectedOrder.courier_charges?.toFixed(2) ||
                      selectedOrder.courier_charges?.toFixed(2) ||
                      "0.00"}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">COD Charge:</span>{" "}
                  <span className="font-medium">
                    ₹
                    {selectedOrder.shipResponse?.cod_charges?.toFixed(2) ||
                      selectedOrder.CodCharge?.toFixed(2) ||
                      "0.00"}
                  </span>
                </div>
                {selectedOrder.shipResponse?.total_charges && (
                  <div>
                    <span className="text-gray-600">Total Charges:</span>{" "}
                    <span className="font-medium text-green-600">
                      ₹{selectedOrder.shipResponse.total_charges.toFixed(2)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Product Details */}
            <div className="border-b pb-4">
              <h3 className="font-semibold mb-2">Product Details</h3>
              {selectedOrder.ProductDetails &&
              selectedOrder.ProductDetails.length > 0 ? (
                <div className="space-y-2">
                  {selectedOrder.ProductDetails.map((product, idx) => (
                    <div
                      key={idx}
                      className="p-3 bg-gray-50 rounded border border-gray-200"
                    >
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div className="col-span-2">
                          <span className="text-gray-600">Product Name:</span>{" "}
                          <span className="font-medium">{product.name}</span>
                          <div className="mt-1">
                            <span className="text-gray-600">SKU:</span>{" "}
                            <span className="font-medium text-xs">
                              {product.sku}
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div>
                            <span className="text-gray-600">Quantity:</span>{" "}
                            <span className="font-semibold">{product.quantity}</span>
                          </div>
                          <div className="mt-1">
                            <span className="text-gray-600">Price:</span>{" "}
                            <span className="font-medium text-green-600">
                              ₹{product.unit_price?.toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-sm">
                  No product details available
                </p>
              )}
            </div>

            {/* Payment Info */}
            <div className="border-b pb-4">
              <h3 className="font-semibold mb-2">Payment Information</h3>

              {selectedOrder.paymentMethods &&
              selectedOrder.paymentMethods.length > 0 ? (
                <div className="space-y-3">
                  {selectedOrder.paymentMethods.map((method, idx) => (
                    <div
                      key={idx}
                      className="p-3 bg-blue-50 rounded border border-blue-200"
                    >
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">Payment Option:</span>{" "}
                          <span className="font-medium">
                            {method.paymentOption}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">Amount:</span>{" "}
                          <span className="font-medium text-green-600">
                            ₹{method.paymentAmount?.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="p-3 bg-green-50 rounded border border-green-200 font-semibold">
                    Total Payment: ₹
                    {selectedOrder.paymentTotalAmount?.toFixed(2) || 0}
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Payment Type:</span>{" "}
                    <span className="font-medium uppercase">
                      {selectedOrder.PaymentType}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Payment Option:</span>{" "}
                    <span className="font-medium">
                      {selectedOrder.paymentOption || "N/A"}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Payment Amount:</span>{" "}
                    <span className="font-medium text-green-600">
                      ₹{selectedOrder.paymentAmount?.toFixed(2) || "0.00"}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Invoice Amount:</span>{" "}
                    <span className="font-medium">
                      ₹{selectedOrder.InvoiceAmount?.toFixed(2) || "0.00"}
                    </span>
                  </div>
                  {selectedOrder.Discount > 0 && (
                    <div>
                      <span className="text-gray-600">Discount:</span>{" "}
                      <span className="font-medium text-red-600">
                        -₹{selectedOrder.Discount?.toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Payment Proof Images */}
            <div>
              <h3 className="font-semibold mb-2">Payment Proof Images</h3>
              {(() => {
                const allImages = getAllPaymentImages(selectedOrder);
                return allImages.length > 0 ? (
                  <div className="grid grid-cols-3 gap-4">
                    {allImages.map((img, index) => (
                      <a
                        key={index}
                        href={`${BACKEND_URL}${img}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="border rounded overflow-hidden hover:shadow-lg transition-shadow"
                      >
                        <img
                          src={`${BACKEND_URL}${img}`}
                          alt={`Payment proof ${index + 1}`}
                          className="w-full h-32 object-cover"
                        />
                      </a>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-400">
                    No payment proof images uploaded
                  </p>
                );
              })()}
            </div>

            {/* Action Buttons */}
            {userType === "admin" && (
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  onClick={() => {
                    handleDeclineClick(selectedOrder._id);
                    setIsModalOpen(false);
                  }}
                  disabled={isDeclining || isVerifying}
                  className="px-6 py-2 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50"
                >
                  Decline
                </button>
                <button
                  onClick={() => {
                    handleVerify(selectedOrder._id);
                    setIsModalOpen(false);
                  }}
                  disabled={isVerifying || isDeclining}
                  className="px-6 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50"
                >
                  {isVerifying ? "Verifying..." : "Verify Payment"}
                </button>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PendingOrder;
